/**
 * The band (05 §3): how each part of the score sounds on the SID voices. The lead is a pulse
 * with slow pulse-width modulation and a delayed vibrato; the chords are one pulse voice
 * cycling the chord's notes at the C64's 50 Hz frame rate; the bass is a plucky filtered pulse;
 * the drums are noise and a pitch-dropping triangle, played on the bass voice's turns. Pure.
 */

import { midiToHz } from '../notes';
import { adsr, FRAME, type VoiceSpec } from '../voice';
import type { Drum, Mix, NoteEvent } from './types';

/** A little air before the next note, as a player lifting a finger (and the SID's hard restart). */
const GAP = 0.018;

function gateFor(ev: NoteEvent, stepSeconds: number): number {
  return Math.max(0.02, ev.len * stepSeconds - GAP);
}

function lead(hz: number, gate: number, level: number): VoiceSpec[] {
  return [{
    wave: 'pulse', at: 0, freq: hz, gate, level,
    env: adsr(0.004, 0.12, 0.7, 0.09),
    width: 0.32,
    pwm: { rate: 1.3, depth: 0.14 },
    // Short notes stay straight; held ones start to sing after a moment, as SID leads did.
    vibrato: { rate: 5.6, depth: 22, delay: Math.min(0.22, gate * 0.5) },
  }];
}

function arp(notes: readonly number[], gate: number, level: number): VoiceSpec[] {
  const steps = notes.map(midiToHz);
  return [{
    wave: 'pulse', at: 0, freq: steps[0] ?? 220, gate, level,
    env: adsr(0.002, 0.18, 0.6, 0.05),
    width: 0.25,
    steps,
    stepTime: FRAME,
  }];
}

function bass(hz: number, gate: number, level: number): VoiceSpec[] {
  return [{
    wave: 'pulse', at: 0, freq: hz, gate, level,
    env: adsr(0.002, 0.14, 0.55, 0.04),
    width: 0.45,
    filter: { type: 'lowpass', freq: 2400, to: 520, sweep: 0.14, q: 6 },
  }];
}

function drum(kind: Drum, level: number): VoiceSpec[] {
  // Struck: each gate lasts its decay, so the release does not cut the decay short.
  switch (kind) {
    case 'kick':
      return [{
        wave: 'triangle', at: 0, freq: 160, to: 45, glide: 0.06, gate: 0.12, env: adsr(0.001, 0.12, 0, 0.03), level,
      }];
    case 'snare':
      return [
        {
          wave: 'noise', at: 0, freq: 5000, to: 2000, glide: 0.1, gate: 0.12, env: adsr(0.001, 0.12, 0, 0.03),
          level: level * 0.8,
        },
        {
          wave: 'triangle', at: 0, freq: 240, to: 160, glide: 0.05, gate: 0.06, env: adsr(0.001, 0.06, 0, 0.02),
          level: level * 0.4,
        },
      ];
    case 'hat':
      return [{
        wave: 'noise', at: 0, freq: 12000, gate: 0.03, env: adsr(0.001, 0.03, 0, 0.01), level: level * 0.4,
        filter: { type: 'highpass', freq: 7000, q: 0 },
      }];
  }
}

/** The voices for one event of the score, `transpose` semitones up (drums never move). */
export function voicesFor(ev: NoteEvent, stepSeconds: number, mix: Mix, transpose = 0): VoiceSpec[] {
  if (ev.drum) return drum(ev.drum, mix.drum);
  const gate = gateFor(ev, stepSeconds);
  const notes = ev.notes.map((n) => n + transpose);
  const first = midiToHz(notes[0] ?? 60);
  switch (ev.part) {
    case 'lead': return lead(first, gate, mix.lead);
    case 'arp': return arp(notes, gate, mix.arp);
    case 'bass': return bass(first, gate, mix.bass);
    case 'drum': return [];
  }
}
